import { formatDistanceToNow, format } from "date-fns";

export function formatIdr(amount: number | null | undefined): string {
  if (amount == null) return "-";
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)} M IDR`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)} K IDR`;
  return `${amount} IDR`;
}

// TiDB returns timestamps like "2026-05-25 08:06:39.014094", no T and no Z.
// JavaScript's Date constructor parses such strings as LOCAL time, but the
// backend stores them as UTC. Without normalization, a message just sent
// shows up as "2 hours ago" for users in UTC+2. Convert space to 'T' and
// append 'Z' when no offset is already present.
//
// Exported so call sites that need a real Date (calendar bucketing,
// before/after filters, datetime-local round-trips, etc.) all go through
// the same UTC-aware parser. Don't use raw `new Date(iso)` on any backend
// timestamp.
// TiDB returns JSON-typed columns (notifications.payload, activity_logs.payload,
// properties.lifestyle_tags) as a string via @tidbcloud/serverless, not the
// parsed object/array we'd expect. Call this on any JSON column before
// stringifying it for display or treating it like an object.
export function parseBackendJson<T = unknown>(raw: unknown): T | null {
  if (raw == null) return null;
  if (typeof raw !== "string") return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

// Render a human-readable identity label for a conversation/contact when
// we don't have a display_name yet. Tries in order: real phone (with +),
// then a "WhatsApp ID xxxx" / "Instagram chat xxxx" hint based on the
// transport id, then a generic placeholder.
export function renderIdentityFallback(input: {
  primary_phone?: string | null;
  external_thread_id?: string | null;
  channel?: string;
}): string {
  if (input.primary_phone) return `+${input.primary_phone}`;
  const ext = input.external_thread_id ?? "";
  if (ext.endsWith("@lid")) {
    // Hidden-number WhatsApp lead. Show last 6 chars of the lid so it's
    // recognisable but doesn't look like a phone.
    const lid = ext.replace(/@lid$/, "");
    return `WhatsApp lead ${lid.slice(-6)}`;
  }
  const match = ext.match(/^(\d+)@(c\.us|s\.whatsapp\.net)$/);
  if (match) return `+${match[1]}`;
  if (ext && input.channel === "instagram") return `IG chat ${ext.slice(-6)}`;
  if (ext && input.channel === "messenger") return `Messenger chat ${ext.slice(-6)}`;
  return "(no name)";
}

export function parseBackendDate(raw: string): Date {
  const hasOffset = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(raw);
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  return new Date(hasOffset ? normalized : `${normalized}Z`);
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return formatDistanceToNow(parseBackendDate(iso), { addSuffix: true });
  } catch {
    return iso ?? "-";
  }
}

export function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return format(parseBackendDate(iso), "yyyy-MM-dd HH:mm");
  } catch {
    return iso ?? "-";
  }
}
